import styled from "@emotion/styled";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Check, Flag, Trophy } from "lucide-react";

import { useMatchFlow } from "@/features/match/useMatchFlow";
import { useTranslations } from "@/i18n";
import { useMatchCount } from "@/store/selectors";
import { isFreeForAll, isTeams, isTournament } from "@/types";
import { BrandMenu } from "@/ui/BrandMenu";
import { theme } from "@/ui/theme";

import { ActivityStage } from "./ActivityStage";
import { LineupStage } from "./LineupStage";
import { PlayStage } from "./PlayStage";
import { RecapStage } from "./RecapStage";
import { TournamentStage } from "./TournamentStage";

// ── Rail definitions per format ──────────────────────────────

type RailStep = {
  id: string;
  labelKey: "railGame" | "railPodium" | "railRecap" | "railLineup" | "railBracket";
};

const RAILS: Record<string, RailStep[]> = {
  default: [{ id: "activity", labelKey: "railGame" }],
  ffa: [
    { id: "activity", labelKey: "railGame" },
    { id: "play", labelKey: "railPodium" },
    { id: "recap", labelKey: "railRecap" },
  ],
  teams: [
    { id: "activity", labelKey: "railGame" },
    { id: "lineup", labelKey: "railLineup" },
    { id: "play", labelKey: "railPodium" },
    { id: "recap", labelKey: "railRecap" },
  ],
  tournament: [
    { id: "activity", labelKey: "railGame" },
    { id: "tournament", labelKey: "railBracket" },
    { id: "recap", labelKey: "railRecap" },
  ],
};

const phaseToStageId = (phase: string): string => {
  if (phase === "idle" || phase === "routing") return "activity";
  if (phase === "setupTeams") return "lineup";
  if (phase === "recording") return "play";
  if (phase === "reviewing") return "recap";
  if (phase.startsWith("tournament")) return "tournament";
  return "activity";
};

// ── Styled ───────────────────────────────────────────────────

const Shell = styled.div`
  position: absolute;
  max-width: 28rem;
  width: 100%;
  height: 100svh;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  padding: 1.5rem 1rem 0;

  ${theme.bp.sm} {
    padding: 1.5rem 1.5rem 0;
  }
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
`;

const BrandWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const BrandText = styled.div`
  & > div:first-of-type {
    font-family: ${theme.fonts.display};
    font-size: 1.125rem;
    line-height: 1;
  }
  & > div:last-of-type {
    font-size: 0.625rem;
    text-transform: uppercase;
    letter-spacing: 0.2em;
    color: ${theme.colors.textMuted};
  }
`;

const MatchCountEl = styled.div`
  text-align: right;
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${theme.colors.textMuted};
`;

const RailNav = styled.nav`
  margin-bottom: 1.5rem;
`;

const RailBar = styled.ol`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  list-style: none;
`;

const RailSegment = styled.li`
  flex: 1;
`;

const RailDot = styled.div<{ state: "done" | "active" | "pending" }>`
  height: 0.375rem;
  border-radius: ${theme.radii.full};
  transition: all 0.3s;
  background: ${(p) =>
    p.state === "done"
      ? theme.colors.primary
      : p.state === "active"
        ? theme.gradients.primary
        : theme.colors.border};
  ${(p) => p.state === "active" && `box-shadow: ${theme.shadow.glow};`}
`;

const RailLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.375rem;
`;

const RailLabel = styled.span<{ active?: boolean }>`
  font-size: 0.625rem;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: ${(p) => (p.active ? theme.colors.text : theme.colors.textMuted)};
  font-weight: ${(p) => (p.active ? 600 : 400)};
`;

const Main = styled.main`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  position: relative;
  isolation: isolate;
`;

const Footer = styled.footer`
  flex-shrink: 0;
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem 0;
  margin-bottom: env(safe-area-inset-bottom);
`;

const BackBtn = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  border-radius: ${theme.radii.xl};
  background: ${theme.colors.secondary};
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  &:hover {
    background: ${theme.colors.secondaryHover};
  }
`;

const PrimaryBtn = styled(motion.button)`
  all: unset;
  cursor: pointer;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border-radius: ${theme.radii.xl};
  background: ${theme.gradients.primary};
  padding: 0.625rem 1rem;
  font-family: ${theme.fonts.display};
  font-size: 1rem;
  color: ${theme.colors.primaryFg};
  box-shadow: ${theme.shadow.glow};
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ── Component ────────────────────────────────────────────────

export const Arena = () => {
  const { t } = useTranslations();
  const flow = useMatchFlow();
  const matchCount = useMatchCount();

  const railKey = !flow.activity
    ? "default"
    : isFreeForAll(flow.activity)
      ? "ffa"
      : isTeams(flow.activity)
        ? "teams"
        : isTournament(flow.activity)
          ? "tournament"
          : "default";

  const stages = RAILS[railKey]!;
  const currentStageId = phaseToStageId(flow.phase);
  const activeIndex = Math.max(
    0,
    stages.findIndex((s) => s.id === currentStageId),
  );

  // ── Footer helpers ──

  const handleRecordingSubmit = () => {
    let topK = 0;
    if (flow.activity && (isFreeForAll(flow.activity) || isTeams(flow.activity))) {
      topK = flow.activity.topK ?? 0;
    }
    const kLimit = topK > 0;
    const kReached = kLimit && flow.rankEntries.length >= topK;
    if (kLimit && kReached) {
      let hasIncremented = false;
      flow.teams.forEach((_, i) => {
        if (flow.rankOf(i) === 0) {
          flow.assignRank(i, hasIncremented);
          hasIncremented ||= true;
        }
      });
    }
    flow.submitResults();
  };

  const recordingDisabled = (() => {
    if (flow.phase !== "recording") return false;
    let topK = 0;
    if (flow.activity && (isFreeForAll(flow.activity) || isTeams(flow.activity))) {
      topK = flow.activity.topK ?? 0;
    }
    const kLimit = topK > 0;
    const kReached = kLimit && flow.rankEntries.length >= topK;
    return !flow.allRanked && !kReached;
  })();

  return (
    <Shell>
      <Header>
        <BrandWrap>
          <BrandMenu />
          <BrandText>
            <div>{t("general.brandName")}</div>
            <div>MMXXVI</div>
          </BrandText>
        </BrandWrap>
        <MatchCountEl>{t("arena.matchCount", matchCount)}</MatchCountEl>
      </Header>

      <RailNav aria-label="Match progress">
        <RailBar>
          {stages.map((s, i) => (
            <RailSegment key={s.id}>
              <RailDot
                state={i < activeIndex ? "done" : i === activeIndex ? "active" : "pending"}
              />
            </RailSegment>
          ))}
        </RailBar>
        <RailLabels>
          {stages.map((s, i) => (
            <RailLabel key={s.id} active={i === activeIndex}>
              {t(`arena.${s.labelKey}`)}
            </RailLabel>
          ))}
        </RailLabels>
      </RailNav>

      <Main>
        <AnimatePresence mode="wait">
          <motion.div
            key={flow.phase.startsWith("tournament") ? "tournament" : flow.phase}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            {(flow.phase === "idle" || flow.phase === "routing") && <ActivityStage flow={flow} />}
            {flow.phase === "setupTeams" && <LineupStage flow={flow} />}
            {flow.phase === "recording" && <PlayStage flow={flow} />}
            {(flow.phase === "tournament.setup" ||
              flow.phase === "tournament.inProgress" ||
              flow.phase === "tournament.completed") && <TournamentStage flow={flow} />}
            {flow.phase === "reviewing" && <RecapStage flow={flow} />}
          </motion.div>
        </AnimatePresence>
      </Main>

      {flow.phase !== "idle" && flow.phase !== "routing" && (
        <Footer>
          <BackBtn onClick={flow.back}>
            <ArrowLeft size={14} />
            {flow.phase === "reviewing" ? t("arena.changeResult") : t("arena.back")}
          </BackBtn>

          {flow.phase === "setupTeams" && (
            <PrimaryBtn
              whileTap={{ scale: 0.98 }}
              disabled={!flow.canConfirmTeams}
              onClick={flow.confirmTeams}
            >
              {t("arena.recordResults")}
            </PrimaryBtn>
          )}

          {flow.phase === "recording" && (
            <PrimaryBtn
              whileTap={{ scale: 0.98 }}
              disabled={recordingDisabled}
              onClick={handleRecordingSubmit}
            >
              <Trophy size={16} /> {t("arena.recordResult")}
            </PrimaryBtn>
          )}

          {flow.phase === "reviewing" && (
            <PrimaryBtn whileTap={{ scale: 0.98 }} onClick={flow.confirmResults}>
              <Check size={16} /> {t("arena.confirmSave")}
            </PrimaryBtn>
          )}

          {flow.phase.startsWith("tournament") && (
            <PrimaryBtn
              whileTap={{ scale: 0.98 }}
              disabled={!flow.canSubmitTournamentResults}
              onClick={flow.submitTournamentResults}
            >
              <Flag size={16} />
              {flow.canSubmitTournamentResults
                ? t("arena.crownChampion")
                : t("arena.finishTournament")}
            </PrimaryBtn>
          )}
        </Footer>
      )}
    </Shell>
  );
};
